// src/store/slices/vehicleSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchVehicles = createAsyncThunk(
  'vehicles/fetchVehicles',
  async ({ page = 1, perPage = 10, search = '', sort = '' }) => {
    const response = await api.get(`/api/vehicles`, {
      params: { page, perPage, search, sort }
    });
    return response.data;
  }
);

export const addVehicle = createAsyncThunk(
  'vehicles/addVehicle',
  async (vehicleData) => {
    const response = await api.post('/api/vehicles', vehicleData);
    return response.data;
  }
);

export const updateVehicle = createAsyncThunk(
  'vehicles/updateVehicle',
  async ({ id, data }) => {
    const response = await api.put(`/api/vehicles/${id}`, data);
    return response.data;
  }
);

export const deleteVehicle = createAsyncThunk(
  'vehicles/deleteVehicle',
  async (id) => {
    await api.delete(`/api/vehicles/${id}`);
    return id;
  }
);

const vehicleSlice = createSlice({
  name: 'vehicles',
  initialState: {
    items: [],
    status: 'idle',
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10
    },
    isSubmitting: false
  },
  reducers: {
    clearVehicleError: (state) => {
      state.error = null;
    },
    setSubmittingStatus: (state, action) => {
      state.isSubmitting = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.vehicles;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(addVehicle.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(addVehicle.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.status = 'succeeded';
        
        // Update pagination info
        state.pagination.totalItems += 1;
        state.pagination.totalPages = Math.ceil(
          state.pagination.totalItems / state.pagination.perPage
        );

        // If we're on the first page, add the new vehicle to the list
        if (state.pagination.currentPage === 1) {
          // Add new vehicle at the beginning without removing any existing items
          state.items = [action.payload, ...state.items];
          
          // If the page is now over capacity, remove the last item
          if (state.items.length > state.pagination.perPage) {
            state.items = state.items.slice(0, state.pagination.perPage);
          }
        }
        // If we're not on the first page, we don't modify the items array
        // as the new item won't be visible on the current page
      })
      .addCase(addVehicle.rejected, (state, action) => {
        state.isSubmitting = false;
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.pagination.totalItems -= 1;
      });
  }
});

export const { clearVehicleError } = vehicleSlice.actions;
export default vehicleSlice.reducer;